import { Fragment } from 'react';

import { CloseOutlineIc } from '@dsch/dd-icons';

import {
  CloseIconContainer,
  CountContainer,
  MainText,
  SelectButton,
  TextContainer,
} from '../../../../../../components/SearchPage/features/makeModel/components/Selected/Selected.styled';

import type { MakeModel } from '../../../../../../components/SearchPage/features/makeModel/MakeModel.typed';

import { Spacer } from '../../../../../../helpers/genericStyles';
import { theme } from '../../../../../../helpers/Theme';
import { accessibleOnClick } from '../../../../../../utils/Accessibility';
import { HeightVariant } from '../../../../../../components/Toolkit/Inputs/types';
import { shouldShowTrims } from '../../MakeModel.constants';

type SelectedProps = {
  makeModels: Array<MakeModel>;
  clearSelection: (make: string, model?: string, trim?: string) => void;
  heightVariant?: HeightVariant;
  sectionName?: string;
};

const Selected = (props: SelectedProps) => {
  const { makeModels, clearSelection, heightVariant, sectionName } = props;
  const showTrimsForSection = shouldShowTrims(sectionName);

  return (
    <>
      {makeModels.map(
        ({ make, model, trim, isEditable }, index) =>
          make &&
          !isEditable && (
            <Fragment key={`${make}-${model || 'all'}`}>
              <SelectButton
                ofType="SECONDARY"
                size={heightVariant === 'LARGE' ? 'LARGE' : 'SMALL'}
                data-testid={`selected-make-model-${index}`}
              >
                {(() => {
                  const ALL_MODELS = ' (All models)';
                  const ALL_TRIMS = ' (All trims)';
                  const ALL_MODELS_AND_TRIMS = ' (All models and trims)';
                  const hasNoModels = !model || model.length === 0;
                  const hasNoTrims = !trim || trim.length === 0;
                  const showAllModelsAndTrims =
                    showTrimsForSection && hasNoModels && hasNoTrims;

                  const firstModel =
                    model && model.length > 0 ? model[0] : ALL_MODELS;
                  const firstTrim =
                    trim && trim.length > 0 ? ` ${trim[0]}` : ALL_TRIMS;

                  const hasMultipleModels = model && model.length > 1;
                  const hasMultipleTrims =
                    showTrimsForSection && trim && trim.length > 1;
                  const showCounts = hasMultipleModels || hasMultipleTrims;

                  const modelCount = hasMultipleModels
                    ? ` (+${model.length - 1})`
                    : '';
                  const trimCount = hasMultipleTrims
                    ? ` (+${trim.length - 1})`
                    : '';

                  return (
                    <TextContainer>
                      <MainText>
                        {make}
                        {showAllModelsAndTrims ? (
                          ALL_MODELS_AND_TRIMS
                        ) : (
                          <>
                            &nbsp;
                            {firstModel}
                            {showTrimsForSection && firstTrim}
                          </>
                        )}
                      </MainText>
                      {showCounts && (
                        <CountContainer>
                          {modelCount}
                          {trimCount}
                        </CountContainer>
                      )}
                    </TextContainer>
                  );
                })()}
                <CloseIconContainer>
                  <CloseOutlineIc
                    height={16}
                    width={16}
                    color={theme.colors.GREY_DARKER}
                    {...accessibleOnClick(
                      () =>
                        clearSelection(
                          make,
                          model && model.length > 0 ? model[0] : undefined,
                          showTrimsForSection && trim && trim.length > 0
                            ? trim[0]
                            : undefined,
                        ),
                      'button',
                      0,
                      'close',
                    )}
                  />
                </CloseIconContainer>
              </SelectButton>
              <Spacer marginBottom="M16" />
            </Fragment>
          ),
      )}
    </>
  );
};

export { Selected };
